import classnames from 'classnames/bind'
import { useTransition } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslate } from 'react-polyglot'
import { ALERT_CODES } from '~/@types/alert'
import { GlobalCheckboxPreset } from '~/@types/checkbox-preset'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalInputPreset } from '~/@types/input-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import RichText, { RichTextBlock } from '~/components/Abstracts/RichText'
import Checkbox from '~/components/Form/Checkbox'
import ErrorList from '~/components/Form/ErrorList'
import Input from '~/components/Form/Input'
import RecaptchaHandler from '~/components/Form/RecaptchaHandler'
import SquaredCta from '~/components/UI/SquaredCta'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { usePopIn } from '~/providers/PopInProvider'
import { useStyle } from '~/providers/StyleProvider'

import useAlerts from '~/hooks/useAlerts'
import useStockAlertPopin from '~/hooks/useStockAlertPopin'

import { FORM } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type AlertStockPopinProps = {
  className?: string
  header?: string
  title?: RichTextBlock
  text?: RichTextBlock
  variantId?: string
  productImage: string
  redirectLink: string
  productName?: string
  onSuccess?(): void
  productId?: string
}

function AlertStockPopin({
  className,
  onSuccess,
  variantId,
  productId,
  productName,
  ...stockAlertProps
}: AlertStockPopinProps) {
  const {
    global: {
      popins: { alertStock },
    },
  } = useGlobalData()
  const t = useTranslate()
  const triggerAlert = useAlerts()
  const { removeCurrent } = usePopIn()

  const [isPending, startTransition] = useTransition()

  const headerStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskMedium,
    color: GlobalThemeColors.Gray,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Title24SangBleuLight,
    color: GlobalThemeColors.Black,
  })

  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Label12SangBleu,
    textStyling: GlobalTextStyling.Italic,
    color: GlobalThemeColors.Gray,
  })

  const handleSuccess = () => {
    startTransition(() => {
      triggerAlert(ALERT_CODES.ALERT_STOCK_SUCCESS)
      removeCurrent()
    })
  }

  const handleError = () => {
    startTransition(() => {
      triggerAlert()
    })
  }

  const { form, onSubmit } = useStockAlertPopin(
    {
      defaultValues: {
        redirectLink: stockAlertProps?.redirectLink,
        variantId,
        productId,
      },
    },
    handleSuccess,
    handleError,
  )

  return (
    <div className={cx(css.AlertStockPopin, className)}>
      {alertStock?.header && (
        <div className={cx(css.header, headerStyle)}>
          {alertStock.header?.replace('%{value}', `${productName}`)}
        </div>
      )}
      <div className={css.content}>
        <RichText
          className={cx(css.title, titleStyle)}
          render={alertStock?.title}
        />
        <RichText
          className={cx(css.text, textStyle)}
          render={alertStock?.text}
        />
      </div>
      <RecaptchaHandler>
        <FormProvider {...form}>
          <form onSubmit={onSubmit}>
            <div className={cx(css.inputContainer)}>
              <div className={css.content}>
                <Input
                  preset={GlobalInputPreset.Email}
                  theme={GlobalThemeColors.Gray}
                />
                <Checkbox
                  className={cx(css.marketing)}
                  preset={GlobalCheckboxPreset.AcceptsTerms}
                  isRound
                  isSquared={false}
                />
                <ErrorList className={cx(css.errorList)} />
              </div>
              <SquaredCta
                className={cx(css.button)}
                isLoading={isPending}
                theme={GlobalThemeColors.Black}
                withBackground
                withBorder={false}
                onClick={onSubmit}>
                {t(FORM.SUBMIT)}
              </SquaredCta>
            </div>
          </form>
        </FormProvider>
      </RecaptchaHandler>
    </div>
  )
}

AlertStockPopin.defaultProps = {}

export default AlertStockPopin
